import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import { useAuth } from '../contexts/AuthContext';

// Track if launch URL has been handled to prevent loops on remount
let launchUrlHandled = false;

/**
 * Custom hook to handle deep links from iOS Universal Links and Android App Links
 * Supports both custom scheme (lifecoachassist://) and HTTPS deep links
 */
export const useDeepLinks = () => {
  const navigate = useNavigate();
  const { checkForTokens } = useAuth();
  const checkForTokensRef = useRef(checkForTokens);

  // Update ref when checkForTokens changes
  useEffect(() => {
    checkForTokensRef.current = checkForTokens;
  }, [checkForTokens]);

  useEffect(() => {
    // Handle app opened from URL (both Universal Links and App Links)
    const handleAppUrlOpen = async (data) => {
      const url = data.url;
      console.log('App opened with URL:', url);

      try {
        // Parse the URL
        let path = '';
        let params = new URLSearchParams();
        let hash = '';

        if (url.includes('://')) {
          const urlObj = new URL(url);
          hash = urlObj.hash;
          
          // Handle capacitor:// scheme (from Supabase redirects)
          // Format: capacitor://app.lifecoachassist.ai/reset-password
          if (urlObj.protocol === 'capacitor:') {
            path = urlObj.pathname;
            params = new URLSearchParams(urlObj.search);
          }
          // Handle custom scheme (lifecoachassist://path)
          else if (urlObj.protocol === 'lifecoachassist:') {
            path = urlObj.pathname || urlObj.host; // host is used when no pathname
            params = new URLSearchParams(urlObj.search);
          }
          // Handle HTTPS deep links (https://yourdomain.com/path)
          else if (urlObj.protocol === 'https:' || urlObj.protocol === 'http:') {
            path = urlObj.pathname;
            params = new URLSearchParams(urlObj.search);
          }
        }

        // Route based on the path
        if (path) {
          // Remove leading slash if present
          const cleanPath = path.startsWith('/') ? path : `/${path}`;
          
          // Build full path with query parameters and hash
          const queryString = params.toString();
          let fullPath = queryString ? `${cleanPath}?${queryString}` : cleanPath;
          
          if (hash) {
            fullPath += hash;
          }
          
          console.log('Navigating to:', fullPath);
          
          // Navigate to the path
          navigate(fullPath, { replace: true });

          // Manually trigger token check after navigation since AuthContext is outside Router
          // and won't automatically detect the URL change
          if (checkForTokensRef.current) {
            checkForTokensRef.current();
          }
        }
      } catch (error) {
        console.error('Error parsing deep link URL:', error);
      }
    };

    // Add listener for app URL open events
    const listener = CapApp.addListener('appUrlOpen', handleAppUrlOpen);

    // Check if app was launched with a URL
    CapApp.getLaunchUrl().then(result => {
      if (result && result.url && !launchUrlHandled) {
        console.log('App launched with URL:', result.url);
        launchUrlHandled = true;
        handleAppUrlOpen(result);
      }
    });

    // Cleanup listener on unmount
    return () => {
      listener.remove();
    };
  }, [navigate]); // Remove checkForTokens to prevent loops
};

export default useDeepLinks;
